import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  Container,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Button,
  Col,
  Row,
  Label,
  Input,
  FormGroup,
} from "reactstrap";

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";

// API helper and URL helper
import { get, del, put } from "../../helpers/api_helper";
import {
  PRICE_LIST,
  UPDATE_PRICE,
  DELETE_PRICE,
} from "../../helpers/url_helper";

// Toaster
import toastr from "toastr";
import "toastr/build/toastr.min.css";

// React Router
import { Controller, useForm } from "react-hook-form";
import productColumns from "components/Columns/ProductColumns";
import { useNavigate } from "react-router-dom";

const Prices = () => {
  const [prices, setNozzles] = useState([]);

  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState([]);

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedNozzle, setSelectedNozzle] = useState(null);
  const [priceToEdit, setPriceToEdit] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);

  const navigate = useNavigate();
  const toggleEditModal = () => setEditModalOpen(!editModalOpen);
  const updateNozzle = (id, data) => put(UPDATE_PRICE(id), data);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser"));
      const role = Number(authUser?.user?.role);
      if (role !== 1) {
        navigate("/dashboard"); // 🔁 Redirect to dashboard if not admin
      } else {
        setIsAdmin(true);
      }
    } catch {
      navigate("/dashboard"); // 🔁 Redirect to dashboard if error
    }
  }, [navigate]);

  const breadcrumbItems = [
    { title: "valfuel", link: "/" },
    { title: "Price", link: "#" },
  ];

  const toggleAddModal = () => {
    setAddModalOpen(!addModalOpen);
    if (!addModalOpen) reset(); // clear form on open
  };

  async function getNozzleList(page, size, sort = [], search = "") {
    const sortKey = sort[0]?.id;
    const sortby = sort[0]?.desc ? "desc" : "asc";

    return await get(PRICE_LIST, {
      params: { page, size, sortKey, sortby },
    });
  }

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: "",
      stock: "",
      buying_price: "",
      selling_price: "",
    },
  });

  useEffect(() => {
    if (priceToEdit) {
      reset({
        name: priceToEdit.name,
        stock: priceToEdit.stock,
        buying_price: priceToEdit.buying_price,
        selling_price: priceToEdit.selling_price,
      });
    }
  }, [priceToEdit,reset]);

const fetchNozzles = useCallback(async () => {
  try {
    setLoading(true);
    const response = await getNozzleList(pageIndex, pageSize, sortBy, searchText);
    const { items, totalPages } = response.data;
    setNozzles(items);
    setTotalPages(totalPages);
  } catch (error) {
    console.error("Failed to fetch prices:", error);
  } finally {
    setLoading(false);
  }
}, [pageIndex, pageSize, sortBy, searchText]);

 const handleEdit = useCallback((item) => {
  setPriceToEdit(item);
  setEditModalOpen(true);
}, []);

const handleDelete = useCallback((item) => {
  setSelectedNozzle(item);
  setModalOpen(true);
}, []);

  const toggleModal = () => {
    setModalOpen(!modalOpen);
  };

  const confirmDelete = async () => {
    try {
      if (selectedNozzle) {
        await del(DELETE_PRICE(selectedNozzle.ul_id)); // Call delete API
        toastr.success("Nozzle deleted successfully!");
        fetchNozzles(pageIndex, pageSize, sortBy, searchText);
      }
    } catch (error) {
      console.error(
        "Failed to delete nozzle:",
        error?.response?.data || error?.message
      );
      toastr.error("Something went wrong while deleting.");
    }
    setModalOpen(false);
  };

  const onSubmitEdit = async (data) => {
    try {
      await updateNozzle(priceToEdit.ul_id, data);
      toastr.success("Nozzle updated successfully!");
      fetchNozzles();
      toggleEditModal();
    } catch (error) {
      toastr.error("Failed to update nozzle.");
      console.error(error);
    }
  };

  const handleSearchChange = (text) => {
    setPageIndex(1);
    setSearchText(text);
  };

 useEffect(() => {
  fetchNozzles();
}, []);

const columns = useMemo(
  () => productColumns(handleEdit, handleDelete, isAdmin),
  [handleEdit, handleDelete, isAdmin]
);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Price" breadcrumbItems={breadcrumbItems} />
        </Container>

        <Card>
          <CardBody>
            {loading ? (
              <div className="text-center">Loading...</div>
            ) : prices.length > 0 ? (
              <TableContainer
                columns={columns || []}
                data={prices || []}
                isPagination={true}
                isGlobalFilter={true}
                customPageSize={pageSize}
                iscustomPageSize={false}
                isBordered={false}
                controlledPagination={{
                  pageIndex,
                  setPageIndex,
                  pageSize,
                  setPageSize,
                  totalPages,
                }}
                onSortChange={setSortBy}
                searchText={searchText}
                onSearchChange={handleSearchChange}
              />
            ) : (
              <div className="text-center py-5">
                <h5>No Record Found</h5>
                {isAdmin && (
                  <Button
                    color="primary"
                    className="mt-3"
                    onClick={toggleAddModal}
                  >
                    Add New Price
                  </Button>
                )}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={modalOpen} toggle={toggleModal}>
        <ModalHeader toggle={toggleModal}>Confirm Deletion</ModalHeader>
        <ModalBody>
          Are you sure you want to delete this nozzle permanently?
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleModal}>
            Cancel
          </Button>
          <Button color="danger" onClick={confirmDelete}>
            Yes, Delete
          </Button>
        </ModalFooter>
      </Modal>

      <Modal isOpen={editModalOpen} toggle={toggleEditModal}>
        <ModalHeader toggle={toggleEditModal}>Add New Price</ModalHeader>
        <ModalBody>
          <form onSubmit={handleSubmit(onSubmitEdit)}>
            <Row>
              {/* Name */}
              <Col md="12">
                <FormGroup className="mb-3">
                  <Label>
                    Name <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: "Name is required" }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="text"
                        className={`form-control ${
                          errors.name ? "is-invalid" : ""
                        }`}
                        placeholder="Enter fuel type or product name"
                      />
                    )}
                  />
                  {errors.name && (
                    <div className="text-danger">{errors.name.message}</div>
                  )}
                </FormGroup>
              </Col>

              {/* Stock */}
              {!isAdmin && (
              <Col md="12">
                <FormGroup className="mb-3">
                  <Label>
                    Stock <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="stock"
                    control={control}
                    rules={{
                      required: "Stock is required",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Stock must be a valid number",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        type="number"
                        inputMode="numeric"
                        className={`form-control ${
                          errors.stock ? "is-invalid" : ""
                        }`}
                        placeholder="Enter stock quantity"
                      />
                    )}
                  />
                  {errors.stock && (
                    <div className="text-danger">{errors.stock.message}</div>
                  )}
                </FormGroup>
              </Col>
              )}
              {/* Selling Price */}
              <Col md="12">
                <FormGroup className="mb-3">
                  <Label>
                    Buying price <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="buying_price"
                    control={control}
                    rules={{
                      required: "Buying price is required",
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: "Price must be a number (up to 2 decimals)",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        inputMode="decimal"
                        placeholder="e.g. 78.50"
                        className={`form-control ${
                          errors.buying_price ? "is-invalid" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.buying_price && (
                    <div className="text-danger">
                      {errors.buying_price.message}
                    </div>
                  )}
                </FormGroup>
              </Col>
              {/* Selling Price */}
              <Col md="12">
                <FormGroup className="mb-3">
                  <Label>
                    Selling Price <span className="text-danger">*</span>
                  </Label>
                  <Controller
                    name="selling_price"
                    control={control}
                    rules={{
                      required: "Selling price is required",
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: "Price must be a number (up to 2 decimals)",
                      },
                    }}
                    render={({ field }) => (
                      <Input
                        {...field}
                        inputMode="decimal"
                        placeholder="e.g. 78.50"
                        className={`form-control ${
                          errors.selling_price ? "is-invalid" : ""
                        }`}
                      />
                    )}
                  />
                  {errors.selling_price && (
                    <div className="text-danger">
                      {errors.selling_price.message}
                    </div>
                  )}
                </FormGroup>
              </Col>
            </Row>
          </form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggleEditModal}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSubmit(onSubmitEdit)}>
            Update
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default Prices;
